import React from 'react';
import i18n from 'i18next';
import HttpApi from 'i18next-http-backend';
import { initReactI18next, useTranslation, withTranslation, Trans } from 'react-i18next';

var DEFAULT_LOCALE = 'en-GB';
var DEFAULT_NAMESPACE = 'common';
var INCONTEXT_LOCALE = 'ach-UG';
/**
 * *_APP_NAME: These app names are the folder names in S3 bucket where corresponding translations are stored.
 * Also, the name of respective app's Translations repo https://github.com/Unibuddy/translations-{appName}
 */

var WIDGET_APP_NAME = 'widget';
var AMBASSADOR_DASHBOARD_APP_NAME = 'ambassador-dashboard';
var DISCOVER_APP_NAME = 'discover';
var UCAS_APP_NAME = 'ucas';
var LIVE_EVENTS_APP_NAME = 'live-events';
var UNIVERSITY_DASHBOARD_APP_NAME = 'university-dashboard';
var POPCARD_APP_NAME = 'popcard';
var EMAIL_TEMPLATES_APP_NAME = 'email-templates';
var NOTIFICATIONS_APP_NAME = 'notifications';
var CHAT_BUBBLE_APP_NAME = 'chat-bubble';
/**
 * App names Enum for type safety
 */

var APP_NAMES;

(function (APP_NAMES) {
  APP_NAMES["WIDGET"] = "widget";
  APP_NAMES["AMBASSADOR_DASHBOARD"] = "ambassador-dashboard";
  APP_NAMES["DISCOVER"] = "discover";
  APP_NAMES["UCAS"] = "ucas";
  APP_NAMES["LIVE_EVENTS"] = "live-events";
  APP_NAMES["UNIVERSITY_DASHBOARD"] = "university-dashboard";
  APP_NAMES["POPCARD"] = "popcard";
  APP_NAMES["EMAIL_TEMPLATES"] = "email-templates";
  APP_NAMES["NOTIFICATIONS"] = "notifications";
  APP_NAMES["CHAT_BUBBLE"] = "chat-bubble";
})(APP_NAMES || (APP_NAMES = {}));
/**
 * *_PROJECT_NAME: These are project names in Crowdin system
 */


var WIDGET_PROJECT_NAME = 'ub-widget';
var POPCARD_PROJECT_NAME = 'ub-popcard';
var AMBASSADOR_DASHBOARD_PROJECT_NAME = 'ub-ambassador-dashboard';
var EMAIL_TEMPLATES_PROJECT_NAME = 'ub-email-templates';
var NOTIFICATIONS_PROJECT_NAME = 'ub-notifications';
var CHAT_BUBBLE_PROJECT_NAME = 'ub-chat-bubble';
/**
 * Project names Enum for type safety
 */

var CROWDIN_PROJECT_NAMES;

(function (CROWDIN_PROJECT_NAMES) {
  CROWDIN_PROJECT_NAMES["WIDGET"] = "ub-widget";
  CROWDIN_PROJECT_NAMES["POPCARD"] = "ub-popcard";
  CROWDIN_PROJECT_NAMES["AMBASSADOR_DASHBOARD"] = "ub-ambassador-dashboard";
  CROWDIN_PROJECT_NAMES["EMAIL_TEMPLATES"] = "ub-email-templates";
  CROWDIN_PROJECT_NAMES["NOTIFICATIONS"] = "ub-notifications";
  CROWDIN_PROJECT_NAMES["CHAT_BUBBLE"] = "ub-chat-bubble";
})(CROWDIN_PROJECT_NAMES || (CROWDIN_PROJECT_NAMES = {}));

var constants = {
  __proto__: null,
  DEFAULT_LOCALE: DEFAULT_LOCALE,
  DEFAULT_NAMESPACE: DEFAULT_NAMESPACE,
  INCONTEXT_LOCALE: INCONTEXT_LOCALE,
  WIDGET_APP_NAME: WIDGET_APP_NAME,
  AMBASSADOR_DASHBOARD_APP_NAME: AMBASSADOR_DASHBOARD_APP_NAME,
  DISCOVER_APP_NAME: DISCOVER_APP_NAME,
  UCAS_APP_NAME: UCAS_APP_NAME,
  LIVE_EVENTS_APP_NAME: LIVE_EVENTS_APP_NAME,
  UNIVERSITY_DASHBOARD_APP_NAME: UNIVERSITY_DASHBOARD_APP_NAME,
  POPCARD_APP_NAME: POPCARD_APP_NAME,
  EMAIL_TEMPLATES_APP_NAME: EMAIL_TEMPLATES_APP_NAME,
  NOTIFICATIONS_APP_NAME: NOTIFICATIONS_APP_NAME,
  CHAT_BUBBLE_APP_NAME: CHAT_BUBBLE_APP_NAME,
  get APP_NAMES () { return APP_NAMES; },
  WIDGET_PROJECT_NAME: WIDGET_PROJECT_NAME,
  POPCARD_PROJECT_NAME: POPCARD_PROJECT_NAME,
  AMBASSADOR_DASHBOARD_PROJECT_NAME: AMBASSADOR_DASHBOARD_PROJECT_NAME,
  EMAIL_TEMPLATES_PROJECT_NAME: EMAIL_TEMPLATES_PROJECT_NAME,
  NOTIFICATIONS_PROJECT_NAME: NOTIFICATIONS_PROJECT_NAME,
  CHAT_BUBBLE_PROJECT_NAME: CHAT_BUBBLE_PROJECT_NAME,
  get CROWDIN_PROJECT_NAMES () { return CROWDIN_PROJECT_NAMES; }
};

function hasInContext(url) {
  if (url === void 0) {
    url = window.location.href;
  }

  var queryString = new URL(url).search;
  var urlSearchParams = new URLSearchParams(queryString);
  return urlSearchParams.get('incontext') === 'true';
}
function setInContextScripts(projectName) {
  // add global vars
  var globalWindow = window;
  globalWindow._jipt = [];

  globalWindow._jipt.push(['project', projectName]); // add crowdin script


  var inContextScript = document.getElementById('in-context-editor');
  inContextScript.src = '//cdn.crowdin.com/jipt/jipt.js';
}

var Intl = function Intl(_ref) {
  var engine = _ref.engine,
      crowdinProjectName = _ref.crowdinProjectName,
      children = _ref.children;

  var _React$useState = React.useState(false),
      ready = _React$useState[0],
      setReady = _React$useState[1];

  var initInContextEditing = React.useCallback(function () {
    setInContextScripts(crowdinProjectName); // change to in-context pseudo-language

    i18n.changeLanguage(INCONTEXT_LOCALE, function (err) {
      if (err) {
        console.warn('%cInContextTranslation is not set. There might be a problem initializing the inContext translation.', 'color: red');
      }

      setReady(true);
    });
  }, [crowdinProjectName]);
  React.useEffect(function () {
    engine.init(function (value) {
      if (value.ready && hasInContext()) {
        initInContextEditing();
      } else {
        setReady(true);
      }
    });
  }, [engine, initInContextEditing]);

  if (!ready) {
    return React.createElement("div", null, "Loading...");
  }

  return React.createElement(React.Fragment, null, children);
};

var I18nextEngine = /*#__PURE__*/function () {
  function I18nextEngine(options) {
    this._appName = options.appName;
    this._translationsUrl = options.translationsUrl;
    this._lng = options.lng || DEFAULT_LOCALE;
    this._defaultNS = options.defaultNS || DEFAULT_NAMESPACE;
  }

  var _proto = I18nextEngine.prototype;

  _proto.init = function init(callback) {
    var httpBackend = {
      crossDomain: true,
      loadPath: this._translationsUrl + "/namespaces/" + this._appName + "/{{lng}}/{{ns}}.json",
      parse: function parse(data) {
        return JSON.parse(data);
      }
    };
    i18n.use(HttpApi).use(initReactI18next).init({
      lng: this._lng,
      load: 'currentOnly',
      fallbackLng: 'en-GB',
      ns: this._defaultNS,
      defaultNS: this._defaultNS,
      interpolation: {
        escapeValue: false
      },
      backend: httpBackend,
      react: {
        bindI18n: 'languageChanged',
        useSuspense: false
      },
      debug: process.env.NODE_ENV === 'development',
      parseMissingKeyHandler: function parseMissingKeyHandler(key) {
        return key;
      },
      returnEmptyString: false
    }, function (err) {
      if (err) {
        console.warn('%cError initializing i18n. Default values will be shown.', 'color: red');
      }

      callback({
        ready: true
      });
    });
  };

  return I18nextEngine;
}();

export default Intl;
export { constants as Constants, I18nextEngine, Trans, i18n, useTranslation, withTranslation };
//# sourceMappingURL=intl.esm.js.map
